import {
	type ForwardedRef,
	forwardRef,
	type HTMLAttributes,
	type ReactNode,
	useState
} from 'react'
import { Image01Icon } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/helpers/utils'

export const EmptyImagePlaceholder = () => (
	<div className='flex aspect-square h-full w-full items-center justify-center rounded-lg bg-neutral-200'>
		<Image01Icon />
	</div>
)

const GalleryImageContent = ({
	imageState,
	topLeftSlot,
	topRightSlot,
	bottomLeftSlot,
	bottomRightSlot,
	hasBlur,
	centerSlot
}: {
	imageState: 'loading' | 'error' | 'loaded'
	topLeftSlot?: ReactNode
	topRightSlot?: ReactNode
	bottomLeftSlot?: ReactNode
	bottomRightSlot?: ReactNode
	hasBlur?: boolean
	centerSlot?: ReactNode
}) => {
	if (imageState === 'loading') {
		return (
			<Skeleton className='aspect-square h-full w-full rounded-lg object-cover' />
		)
	}
	if (imageState === 'error') {
		return <EmptyImagePlaceholder />
	}
	return (
		<>
			{topLeftSlot && (
				<div className='absolute top-4 left-4'>{topLeftSlot}</div>
			)}
			{topRightSlot && (
				<div className='absolute top-4 right-4'>{topRightSlot}</div>
			)}
			{bottomLeftSlot && (
				<div className='absolute bottom-6 left-4'>{bottomLeftSlot}</div>
			)}
			{bottomRightSlot && (
				<div className='absolute right-4 bottom-6'>
					{bottomRightSlot}
				</div>
			)}
			{hasBlur && (
				<div className='absolute inset-0 rounded-lg bg-neutral-white/20 backdrop-blur-md' />
			)}
			{centerSlot && (
				<div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
					{centerSlot}
				</div>
			)}
		</>
	)
}

interface GalleryImageItemProps extends HTMLAttributes<HTMLDivElement> {
	src: string
	hasBlur?: boolean
	topLeftSlot?: ReactNode
	topRightSlot?: ReactNode
	bottomLeftSlot?: ReactNode
	bottomRightSlot?: ReactNode
	centerSlot?: ReactNode
	className?: string
	isSelected?: boolean
}

export const GalleryImageItem = forwardRef<
	HTMLDivElement,
	GalleryImageItemProps
>(
	(
		{
			src,
			hasBlur,
			centerSlot,
			bottomRightSlot,
			topRightSlot,
			bottomLeftSlot,
			topLeftSlot,
			isSelected = false,
			className,
			...rest
		},
		ref: ForwardedRef<HTMLDivElement>
	) => {
		const [imageState, setImageState] = useState<
			'loading' | 'error' | 'loaded'
		>('loading')
		return (
			<div
				ref={ref}
				{...rest}
				className={cn(
					'relative aspect-square w-full rounded-lg object-cover [&:hover_.show-on-hover]:opacity-100 [&_.show-on-hover]:opacity-0 [&_.show-on-hover]:transition-opacity [&_.show-on-hover]:duration-150 [&_.show-on-hover]:ease-in-out',
					rest.onClick && 'cursor-pointer',
					isSelected &&
						imageState !== 'loading' &&
						imageState !== 'error' &&
						'outline-2 outline-neutral-900',
					className
				)}
			>
				{/* biome-ignore lint/correctness/useImageSize: the image is from backend and the size is not known */}
				{/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: onLoad/onError are media lifecycle events, not interactive handlers */}
				<img
					alt={'Gallery item'}
					className='aspect-square w-full rounded-lg object-cover'
					hidden={imageState === 'loading' || imageState === 'error'}
					onError={() => setImageState('error')}
					onLoad={() => setImageState('loaded')}
					src={src}
				/>
				<GalleryImageContent
					bottomLeftSlot={bottomLeftSlot}
					bottomRightSlot={bottomRightSlot}
					centerSlot={centerSlot}
					hasBlur={hasBlur}
					imageState={imageState}
					topLeftSlot={topLeftSlot}
					topRightSlot={topRightSlot}
				/>
			</div>
		)
	}
)
